import { CloseOutlined, CheckOutlined, EyeOutlined } from '@ant-design/icons';
import {
	Button,
	Card,
	Col,
	message,
	Modal,
	Row,
	Spin,
	Typography,
	Input,
	Table,
	Tooltip,
} from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import moment from 'moment';
import { Box } from 'atoms';
import { ACL } from 'components/common';
import FilePreviewModal from 'componentsv2/common/FilePreviewModal';
import { getVendorContractByIdV2 } from 'store/vendorSliceV2';
import {
	approveVendorContract,
	rejectVendorContract,
	getApprovalDetailsV2,
} from 'store/approvalSliceV2';

const { Title, Text } = Typography;

const PageContainer = styled(Box)`
	padding: 24px;
	background: ${(props) => (props.fromApproval ? '#fff' : '#f5f5f5')};
	min-height: calc(100vh - 64px);
`;

const HeaderContainer = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 24px;
`;

const HeaderLeft = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;
`;

const HeaderRight = styled.div`
	display: flex;
	gap: 12px;
`;

const ContractId = styled(Title)`
	margin: 0 !important;
	font-size: 20px;
	font-weight: 600;
`;

const CloseIconButton = styled(Button)`
	border: none;
	box-shadow: none;
	padding: 4px;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;

	&:hover {
		background-color: #f5f5f5;
	}
`;

const ActionButtonContainer = styled.div`
	display: flex;
	justify-content: center;
	gap: 16px;
	margin-top: 32px;
	padding-top: 24px;
	border-top: 1px solid #e8e8e8;
`;

const RejectButtonStyled = styled(Button)`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 8px 24px;
	height: 40px;
	background: #fff;
	color: #f44336;
	border: 1.5px solid #f44336;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 600;
	min-width: 120px;

	&:hover {
		background: #ffebee;
		border-color: #e57373;
		color: #e57373;
	}

	&:active {
		background: #fff;
		border-color: #d32f2f;
		color: #d32f2f;
	}
`;

const ApproveButtonStyled = styled(Button)`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 8px 24px;
	height: 40px;
	background: #52c41a;
	color: #fff;
	border: 1.5px solid #52c41a;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 600;
	min-width: 120px;

	&:hover {
		background: #73d13d;
		border-color: #73d13d;
	}

	&:active {
		background: #389e0d;
		border-color: #389e0d;
	}
`;

const DetailsCard = styled(Card)`
	border-radius: 8px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	margin-bottom: 24px;
`;

const FieldLabel = styled(Text)`
	font-size: 12px;
	color: #666;
	display: block;
	margin-bottom: 4px;
`;

const FieldValue = styled(Text)`
	font-size: 14px;
	color: #000;
	font-weight: 500;
	display: block;
`;

const { TextArea } = Input;

const APPROVALS_VENDOR_CONTRACT_PATH = '/approvals/vendor-contract';

/**
 * Format date to readable string
 */
const formatDate = (dateString) => {
	if (!dateString) return '-';
	return moment(dateString).format('DD-MM-YYYY');
};

const MONEY_FIELDS = new Set([
	'vehicle_cost',
	'loader_cost',
	'delivery_associate_cost',
	'extra_charges_per_km',
	'extra_charges_per_hour',
	'extra_charges_per_trip',
	'charges_per_km',
	'other_expenses_value',
	'fixed_rate_per_day',
]);

const titleCase = (key = '') =>
	key
		.replace(/_/g, ' ')
		.replace(/\b\w/g, (c) => c.toUpperCase())
		.trim();

const isIsoDateString = (val) => typeof val === 'string' && /^\d{4}-\d{2}-\d{2}(T|$)/.test(val);

// Document keys are stored as "{uuid}-{uuid}-{original filename}"; strip every
// leading UUID prefix so only the human-readable filename remains.
const UUID_PREFIX = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}-/i;

const stripUuidPrefixes = (key) => {
	let result = String(key);
	while (UUID_PREFIX.test(result)) {
		result = result.replace(UUID_PREFIX, '');
	}
	return result;
};

// Document values can be nested (e.g. { signed_contract_file: { document_key } });
// dig out the first document_key found at any depth.
const findDocumentKey = (obj) => {
	if (!obj || typeof obj !== 'object') return null;
	if (typeof obj.document_key === 'string') return obj.document_key;
	return Object.values(obj).reduce((found, v) => found || findDocumentKey(v), null);
};

const formatFieldValue = (val, field) => {
	if (val === null || val === undefined || val === '') return '-';
	if (typeof val === 'boolean') return val ? 'Yes' : 'No';
	if (isIsoDateString(val)) {
		const m = moment(val);
		if (m.isValid()) return m.format('DD-MM-YYYY');
	}
	if (typeof val === 'object') {
		const docKey = findDocumentKey(val);
		if (docKey) return stripUuidPrefixes(docKey);
		try {
			return JSON.stringify(val);
		} catch (e) {
			return String(val);
		}
	}
	if (MONEY_FIELDS.has(field) && Number.isFinite(Number(val))) {
		return `₹${val}`;
	}
	return String(val);
};

const buildEditFlowRows = (fieldDiff, currentValues) => {
	if (!fieldDiff || typeof fieldDiff !== 'object') return [];
	return Object.entries(fieldDiff).map(([field, entry]) => ({
		key: field,
		field,
		currentValue: entry?.old ?? currentValues?.[field],
		proposedValue: entry?.new,
	}));
};

const DocumentEyeIcon = styled(EyeOutlined)`
	color: #1890ff;
	cursor: pointer;
	margin-left: 8px;
	font-size: 16px;
`;

// Render a diff cell. When the value carries a document_key it is a file
// change, so show the (cleaned) filename plus an eye icon that opens the
// document; otherwise fall back to the normal formatted value.
const renderOrderRates = (val) => {
	if (!Array.isArray(val) || val.length === 0) return '-';
	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
			{val.map((line, i) => (
				// eslint-disable-next-line react/no-array-index-key
				<span key={line?.rate_id || `line-${i}`}>
					{line?.rate_id || `Line ${i + 1}`}: ₹{line?.rate}
				</span>
			))}
		</div>
	);
};

const renderValueCell = (val, field, onViewDocument) => {
	if (field === 'order_rates' && Array.isArray(val)) {
		return renderOrderRates(val);
	}
	const docKey = val && typeof val === 'object' ? findDocumentKey(val) : null;
	if (docKey) {
		const fileName = stripUuidPrefixes(docKey);
		return (
			<span>
				{fileName}
				<Tooltip title="View document">
					<DocumentEyeIcon onClick={() => onViewDocument(docKey, fileName)} />
				</Tooltip>
			</span>
		);
	}
	return formatFieldValue(val, field);
};

const getEditFlowColumns = (onViewDocument) => [
	{
		title: 'Field',
		dataIndex: 'field',
		key: 'field',
		render: (text) => titleCase(text),
	},
	{
		title: 'Current Value',
		dataIndex: 'currentValue',
		key: 'currentValue',
		render: (val, record) => renderValueCell(val, record?.field, onViewDocument),
	},
	{
		title: 'Proposed Value',
		dataIndex: 'proposedValue',
		key: 'proposedValue',
		render: (val, record) => renderValueCell(val, record?.field, onViewDocument),
	},
];

/**
 * Format temperature controlled value
 */
const formatTempControlled = (value) => {
	if (value === true || value === 'true' || value === 'Yes' || value === 'yes' || value === 1) {
		return 'Yes';
	}
	if (value === false || value === 'false' || value === 'No' || value === 'no' || value === 0) {
		return 'NO';
	}
	return value || '-';
};

// eslint-disable-next-line sonarjs/cognitive-complexity
function VendorContractDetailsPageV2() {
	const { id } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch();
	const [loading, setLoading] = useState(true);
	const [details, setDetails] = useState(null);
	const [approveModalVisible, setApproveModalVisible] = useState(false);
	const [rejectModalVisible, setRejectModalVisible] = useState(false);
	const [comments, setComments] = useState('');
	const [rejectionReason, setRejectionReason] = useState('');
	const [actionLoading, setActionLoading] = useState(false);
	const [documentPreview, setDocumentPreview] = useState({
		visible: false,
		fileKey: null,
		fileName: '',
	});

	const handleViewDocument = (fileKey, fileName) => {
		if (!fileKey) {
			message.warning('Document not available');
			return;
		}
		setDocumentPreview({ visible: true, fileKey, fileName });
	};

	const closeDocumentPreview = () =>
		setDocumentPreview({ visible: false, fileKey: null, fileName: '' });

	// Check if accessed from approval page
	const isApprovalRoute = location.pathname.startsWith('/approvals/vendor-contract');
	const fromApproval = location.state?.fromApproval || isApprovalRoute;
	const approvalIdFromState = location.state?.approvalId || null;
	const entityIdFromState = location.state?.entityId || null;
	// If accessed from approval route, the id param is the entity_id (contract ID)
	// approval_id is passed in state
	const entityId = isApprovalRoute ? id || entityIdFromState : id;
	const approvalId = approvalIdFromState;

	useEffect(() => {
		if (entityId) {
			if (isApprovalRoute) {
				fetchApprovalDetails();
			} else {
				fetchDetails();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [entityId, isApprovalRoute]);

	/**
	 * Format address object to string
	 * Returns formatted address with other fields (city, state, pincode, zone)
	 * Note: address_line is handled separately in getAddress function
	 */
	const formatAddress = (address) => {
		if (!address) return '-';
		if (typeof address === 'string') return address;
		if (typeof address === 'object') {
			// Format other fields (city, state, pincode, zone) - address_line handled separately
			const parts = [];
			if (address.city) parts.push(address.city);
			if (address.state) parts.push(address.state);
			if (address.pincode) parts.push(address.pincode);
			if (address.zone) parts.push(`Zone: ${address.zone}`);
			return parts.length > 0 ? parts.join(', ') : '-';
		}
		return '-';
	};

	/**
	 * Get POC name - from entity_details, entity_summary, or contract
	 */
	const getPocName = (entityDetails, entitySummary, contract) => {
		if (entityDetails?.poc_name !== undefined && entityDetails.poc_name !== null) {
			return entityDetails.poc_name;
		}
		if (entitySummary?.poc_name !== undefined && entitySummary.poc_name !== null) {
			return entitySummary.poc_name;
		}
		return contract?.poc_name || contract?.contact_person || '-';
	};

	/**
	 * Get POC number - from entity_details, entity_summary, or contract
	 */
	const getPocNumber = (entityDetails, entitySummary, contract) => {
		if (entityDetails?.poc_number !== undefined && entityDetails.poc_number !== null) {
			return entityDetails.poc_number;
		}
		if (entitySummary?.poc_number !== undefined && entitySummary.poc_number !== null) {
			return entitySummary.poc_number;
		}
		return contract?.poc_number || contract?.contact_number || '-';
	};

	/**
	 * Get temperature controlled value with fallback logic
	 */
	const getTempControlledValue = (entityData, entitySummary, contract) => {
		if (entityData?.temperature_controlled !== undefined) {
			return entityData.temperature_controlled;
		}
		if (entitySummary?.temperature_controlled !== undefined) {
			return entitySummary.temperature_controlled;
		}
		if (contract?.temperature_controlled !== undefined) {
			return contract.temperature_controlled;
		}
		if (contract?.temp_controlled !== undefined) {
			return contract.temp_controlled;
		}
		return null;
	};

	/**
	 * Get vendor name - from entity_details, entity_summary, or contract
	 */
	const getVendorName = (entityDetails, entitySummary, contract) =>
		entityDetails?.vendor_name ||
		entitySummary?.vendor_name ||
		contract?.vendor_name ||
		contract?.vendor?.name ||
		'-';

	/**
	 * Get vendor type - from entity_details, entity_summary, or contract
	 */
	const getVendorType = (entityDetails, entitySummary, contract) =>
		entityDetails?.vendor_type ||
		entitySummary?.vendor_type ||
		contract?.vendor_type ||
		contract?.vendor?.type ||
		'-';

	/**
	 * Get legal structure - from entity_details, entity_summary, or contract
	 */
	const getLegalStructure = (entityDetails, entitySummary, contract) =>
		entityDetails?.legal_structure ||
		entitySummary?.legal_structure ||
		contract?.legal_structure ||
		'-';

	/**
	 * Get address - from entity_details, entity_summary, or contract
	 * Maps to address_line key from response
	 */
	const getAddress = (entityDetails, entitySummary, contract) => {
		// Priority: address_line from entity_details > entity_summary > contract
		if (entityDetails?.address?.address_line) return entityDetails.address.address_line;
		if (entitySummary?.address?.address_line) return entitySummary.address.address_line;
		if (contract?.address?.address_line) return contract.address.address_line;
		// Fallback to formatted address if address_line not available
		return (
			formatAddress(entityDetails?.address) ||
			formatAddress(entitySummary?.address) ||
			contract?.address ||
			'-'
		);
	};

	/**
	 * Get billing type - from entity_data (backend sends this)
	 */
	const getBillingType = (entityData, contract) =>
		entityData?.billing_type || contract?.billing_type || contract?.contract_type || '-';

	/**
	 * Get contract date - from entity_data (backend sends this)
	 */
	const getContractDate = (entityData, contract) =>
		entityData?.contract_date || contract?.contract_date || null;

	/**
	 * Get expiry date - from entity_data (backend sends this)
	 */
	const getExpiryDate = (entityData, contract) =>
		entityData?.expiry_date || contract?.expiry_date || null;

	/**
	 * Get capacity tonnes - from entity_data (backend sends this)
	 */
	const getCapacityTonnes = (entityData, contract) => {
		if (entityData?.capacity_tonnes !== undefined) {
			return entityData.capacity_tonnes;
		}
		if (contract?.capacity_tonnes !== undefined) {
			return contract.capacity_tonnes;
		}
		return null;
	};

	/**
	 * Get vehicle type - from contract (backend doesn't send vehicle_type name in entity_data)
	 */
	const getVehicleType = (contract) =>
		contract?.vehicle_type || contract?.vehicle_type_name || '-';

	/**
	 * Get project name - from entity_details, entity_summary, or contract
	 * Maps to project_name key from response
	 */
	const getProjectName = (entityDetails, entitySummary, contract) =>
		entityDetails?.project_name || entitySummary?.project_name || contract?.project_name || '-';

	/**
	 * Get project code - from entity_details, entity_summary, or contract
	 */
	const getProjectCode = (entityDetails, entitySummary, contract) =>
		entityDetails?.project_code || entitySummary?.project_code || contract?.project_code || '-';

	/**
	 * Get route name - from contract (backend doesn't send route name in entity_data)
	 */
	const getRouteName = (contract) => contract?.route_name || '-';

	/**
	 * Get created by - from approval (backend sends this)
	 */
	const getCreatedBy = (approvalData, contract) =>
		approvalData?.approval?.requested_by_name ||
		contract?.created_by ||
		contract?.created_by_name ||
		'-';

	/**
	 * Merge approval data with contract data
	 */
	const mergeApprovalAndContractData = (approvalData, contract) => {
		const entityData = approvalData.approval?.entity_data || {};
		const entitySummary = approvalData.approval?.entity_summary || {};
		const entityDetails = approvalData.entity_details || {};

		return {
			...contract,
			// Edit-flow fields surfaced for the Edit Request card + diff table
			approval_type: approvalData.approval?.approval_type,
			can_decide: approvalData.approval?.can_decide,
			field_diff: entityDetails?.field_diff || entityData?.field_diff || null,
			current_values: entityDetails?.current_values || null,
			editor_name: entityDetails?.editor_name || null,
			submitted_at:
				entityDetails?.submitted_at || approvalData.approval?.requested_at || null,
			editor_comments: entityDetails?.editor_comments || null,
			// Fields from entity_data (backend sends these)
			billing_type: getBillingType(entityData, contract),
			contract_date: getContractDate(entityData, contract),
			expiry_date: getExpiryDate(entityData, contract),
			capacity_tonnes: getCapacityTonnes(entityData, contract),
			vehicle_type_id: entityData.vehicle_type_id || contract?.vehicle_type_id || null,
			temp_controlled: getTempControlledValue(entityData, entitySummary, contract),
			route_id: entityData.route_id || contract?.route_id || null,
			// Fields from entity_details (primary source for approval details) or entity_summary or contract
			vendor_name: getVendorName(entityDetails, entitySummary, contract),
			vendor_type: getVendorType(entityDetails, entitySummary, contract),
			legal_structure: getLegalStructure(entityDetails, entitySummary, contract),
			poc_name: getPocName(entityDetails, entitySummary, contract),
			poc_number: getPocNumber(entityDetails, entitySummary, contract),
			address: getAddress(entityDetails, entitySummary, contract),
			project_name: getProjectName(entityDetails, entitySummary, contract),
			project_code: getProjectCode(entityDetails, entitySummary, contract),
			// Fields from entity_details or contract (backend sends these in approval details)
			vehicle_type: entityDetails?.vehicle_type_name || getVehicleType(contract),
			route_name: entityDetails?.route_name || getRouteName(contract),
			// Fields from approval (backend sends this)
			created_by: getCreatedBy(approvalData, contract),
		};
	};

	const fetchApprovalDetails = async () => {
		setLoading(true);
		try {
			let contract = null;
			let approvalData = null;

			// Fetch approval details first to get entity_data
			if (approvalId) {
				approvalData = await dispatch(getApprovalDetailsV2({ approval_id: approvalId }));
			}

			// Fetch contract details using entity_id
			if (entityId) {
				contract = await dispatch(getVendorContractByIdV2({ id: entityId }));
			}

			// If we have approval data, use entity_summary as primary source
			if (approvalData && approvalData.approval) {
				const mergedDetails = mergeApprovalAndContractData(approvalData, contract);
				setDetails(mergedDetails);
			} else if (contract) {
				// Fallback: use contract data directly
				setDetails(contract);
			} else {
				message.error('Contract or Approval not found');
				navigate(APPROVALS_VENDOR_CONTRACT_PATH);
			}
		} catch (error) {
			console.error('Error fetching approval details:', error);
			message.error('Failed to fetch approval details');
		} finally {
			setLoading(false);
		}
	};

	const fetchDetails = async () => {
		setLoading(true);
		try {
			// Use v2 API to fetch contract by entity_id
			const contract = await dispatch(getVendorContractByIdV2({ id: entityId }));

			if (contract) {
				setDetails(contract);
			} else {
				message.error('Contract not found');
				navigate('/vendor-contracts');
			}
		} catch (error) {
			console.error('Error fetching details:', error);
			message.error('Failed to fetch contract details');
		} finally {
			setLoading(false);
		}
	};

	const handleApproveClick = () => {
		setApproveModalVisible(true);
		setComments('');
	};

	const handleRejectClick = () => {
		setRejectModalVisible(true);
		setRejectionReason('');
	};

	const handleApproveConfirm = async () => {
		if (!approvalId) {
			message.error('Approval ID not found');
			return;
		}

		if (!comments.trim()) {
			message.error('Comments are required for approval');
			return;
		}

		setActionLoading(true);
		try {
			const result = await dispatch(
				approveVendorContract({
					approval_id: approvalId,
					comments,
				})
			);

			if (result.success) {
				setApproveModalVisible(false);
				setComments('');
				message.success('Contract approved successfully');
				navigate(APPROVALS_VENDOR_CONTRACT_PATH);
			}
		} catch (error) {
			console.error('Error approving contract:', error);
			message.error('Failed to approve contract');
		} finally {
			setActionLoading(false);
		}
	};

	const handleRejectConfirm = async () => {
		if (!approvalId) {
			message.error('Approval ID not found');
			return;
		}

		if (!rejectionReason.trim()) {
			message.error('Rejection reason is required');
			return;
		}

		setActionLoading(true);
		try {
			const result = await dispatch(
				rejectVendorContract({
					approval_id: approvalId,
					rejection_reason: rejectionReason,
				})
			);

			if (result.success) {
				setRejectModalVisible(false);
				setRejectionReason('');
				message.success('Contract rejected successfully');
				navigate(APPROVALS_VENDOR_CONTRACT_PATH);
			}
		} catch (error) {
			console.error('Error rejecting contract:', error);
			message.error('Failed to reject contract');
		} finally {
			setActionLoading(false);
		}
	};

	if (loading) {
		return (
			<PageContainer $fromApproval={fromApproval}>
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
					<Spin size="large" tip="Loading contract details..." />
				</Box>
			</PageContainer>
		);
	}

	if (!details) {
		return (
			<PageContainer $fromApproval={fromApproval}>
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
					<Text>Contract not found</Text>
				</Box>
			</PageContainer>
		);
	}

	return (
		<PageContainer $fromApproval={fromApproval}>
			{fromApproval && (
				<HeaderContainer>
					<HeaderLeft>
						<ContractId>Request ID: {approvalId || id}</ContractId>
					</HeaderLeft>
					<HeaderRight>
						<CloseIconButton
							icon={<CloseOutlined />}
							onClick={() => navigate(APPROVALS_VENDOR_CONTRACT_PATH)}
						/>
					</HeaderRight>
				</HeaderContainer>
			)}

			<DetailsCard>
				<Row gutter={[24, 16]}>
					<Col span={8}>
						<FieldLabel>Vendor Name</FieldLabel>
						<FieldValue>
							<strong>
								{details.vendor_name ||
									details.vendor?.name ||
									details.vendor_info?.vendor_name ||
									'-'}
							</strong>
						</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>Project Name</FieldLabel>
						<FieldValue>
							<strong>{details.project_name || '-'}</strong>
						</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>Billing Type</FieldLabel>
						<FieldValue>
							<strong>{details.billing_type || details.contract_type || '-'}</strong>
						</FieldValue>
					</Col>
				</Row>
				<Row gutter={[24, 16]} style={{ marginTop: 16 }}>
					<Col span={8}>
						<FieldLabel>Contract Date</FieldLabel>
						<FieldValue>{formatDate(details.contract_date)}</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>Expiry Date</FieldLabel>
						<FieldValue>{formatDate(details.expiry_date)}</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>Temp Controlled</FieldLabel>
						<FieldValue>
							<strong>{formatTempControlled(details.temp_controlled)}</strong>
						</FieldValue>
					</Col>
				</Row>
				<Row gutter={[24, 16]} style={{ marginTop: 16 }}>
					<Col span={8}>
						<FieldLabel>Vehicle Type</FieldLabel>
						<FieldValue>{details.vehicle_type || '-'}</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>Capacity (Tonnes)</FieldLabel>
						<FieldValue>
							{details.capacity_tonnes !== null &&
							details.capacity_tonnes !== undefined
								? `${details.capacity_tonnes}`
								: '-'}
						</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>Route</FieldLabel>
						<FieldValue>{details.route_name || details.route_id || '-'}</FieldValue>
					</Col>
				</Row>
				<Row gutter={[24, 16]} style={{ marginTop: 16 }}>
					<Col span={8}>
						<FieldLabel>Vendor Type</FieldLabel>
						<FieldValue>
							{details.vendor_type || details.vendor?.type || '-'}
						</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>Legal Structure</FieldLabel>
						<FieldValue>{details.legal_structure || '-'}</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>Address</FieldLabel>
						<FieldValue>{details.address || '-'}</FieldValue>
					</Col>
				</Row>
				<Row gutter={[24, 16]} style={{ marginTop: 16 }}>
					<Col span={8}>
						<FieldLabel>PoC Name</FieldLabel>
						<FieldValue>{details.poc_name || details.contact_person || '-'}</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>PoC Number</FieldLabel>
						<FieldValue>
							{details.poc_number || details.contact_number || '-'}
						</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>Created By</FieldLabel>
						<FieldValue>
							{details.created_by || details.created_by_name || '-'}
						</FieldValue>
					</Col>
				</Row>
			</DetailsCard>

			{details.fixed_rate_per_day != null && (
				<DetailsCard>
					<Row gutter={[24, 16]}>
						<Col span={8}>
							<FieldLabel>Fixed Rate Per Day</FieldLabel>
							<FieldValue>
								<strong>
									₹ {parseFloat(details.fixed_rate_per_day).toFixed(2)}
								</strong>
							</FieldValue>
						</Col>
					</Row>
				</DetailsCard>
			)}

			{details.approval_type === 'vendor_contract_edit' && details.field_diff && (
				<>
					<DetailsCard title="Edit Request" headStyle={{ fontWeight: 600, fontSize: 16 }}>
						<Row gutter={[24, 16]}>
							<Col span={8}>
								<FieldLabel>Submitted By</FieldLabel>
								<FieldValue>{details.editor_name || '-'}</FieldValue>
							</Col>
							<Col span={8}>
								<FieldLabel>Submitted At</FieldLabel>
								<FieldValue>
									{details.submitted_at
										? moment(details.submitted_at).format('DD-MM-YYYY HH:mm')
										: '-'}
								</FieldValue>
							</Col>
							<Col span={8}>
								<FieldLabel>Reason</FieldLabel>
								<FieldValue>{details.editor_comments || '-'}</FieldValue>
							</Col>
						</Row>
					</DetailsCard>

					<DetailsCard>
						<Table
							rowKey="key"
							columns={getEditFlowColumns(handleViewDocument)}
							dataSource={buildEditFlowRows(
								details.field_diff,
								details.current_values
							)}
							pagination={false}
							size="middle"
						/>
					</DetailsCard>
				</>
			)}

			{fromApproval && details?.can_decide === true && (
				<ActionButtonContainer>
					<ACL action="update" module="handle_vendor_contract_approval">
						<ApproveButtonStyled onClick={handleApproveClick} disabled={actionLoading}>
							<CheckOutlined />
							Approve
						</ApproveButtonStyled>
					</ACL>
					<ACL action="update" module="handle_vendor_contract_approval">
						<RejectButtonStyled onClick={handleRejectClick} disabled={actionLoading}>
							<CloseOutlined />
							Reject
						</RejectButtonStyled>
					</ACL>
				</ActionButtonContainer>
			)}

			{/* Approve Modal */}
			<Modal
				title="Approve Vendor Contract"
				open={approveModalVisible}
				onOk={handleApproveConfirm}
				onCancel={() => {
					setApproveModalVisible(false);
					setComments('');
				}}
				okText="Approve"
				cancelText="Cancel"
				confirmLoading={actionLoading}
				okButtonProps={{
					disabled: actionLoading || !comments.trim(),
				}}
			>
				<Box>
					<Text
						display="block"
						style={{
							fontSize: '14px',
							color: '#666',
							marginBottom: 24,
							lineHeight: 1.5,
						}}
					>
						This contract will be approved and you won&apos;t be able to revert this
						change.
					</Text>
					<Box mb={3} textAlign="left">
						<Text
							fontSize="14px"
							color="#333"
							mb={1}
							display="block"
							style={{ textAlign: 'left' }}
						>
							Comments <Text color="#ff4d4f">*</Text>
						</Text>
						<TextArea
							rows={4}
							value={comments}
							onChange={(e) => setComments(e.target.value)}
							placeholder="Enter any comments for this approval..."
							maxLength={1000}
							showCount
							status={!comments.trim() && comments !== '' ? 'error' : ''}
						/>
						{!comments.trim() && comments !== '' && (
							<Text fontSize="12px" color="#ff4d4f" mt={1} display="block">
								Comments are required
							</Text>
						)}
					</Box>
				</Box>
			</Modal>

			{/* Reject Modal */}
			<Modal
				title="Reject Vendor Contract"
				open={rejectModalVisible}
				onOk={handleRejectConfirm}
				onCancel={() => {
					setRejectModalVisible(false);
					setRejectionReason('');
				}}
				okText="Reject"
				cancelText="Cancel"
				confirmLoading={actionLoading}
				okButtonProps={{
					disabled: actionLoading || !rejectionReason.trim(),
					danger: true,
				}}
			>
				<Box>
					<Text fontSize="14px" color="#666" mb={2} display="block">
						Are you sure you want to reject this vendor contract?
					</Text>
					<Box mb={3}>
						<Text fontSize="14px" color="#333" mb={1} display="block">
							Rejection Reason <Text color="#ff4d4f">*</Text>
						</Text>
						<TextArea
							rows={4}
							value={rejectionReason}
							onChange={(e) => setRejectionReason(e.target.value)}
							placeholder="Enter reason for rejection..."
							maxLength={1000}
							showCount
							status={
								!rejectionReason.trim() && rejectionReason !== '' ? 'error' : ''
							}
						/>
						{!rejectionReason.trim() && rejectionReason !== '' && (
							<Text fontSize="12px" color="#ff4d4f" mt={1} display="block">
								Rejection reason is required
							</Text>
						)}
					</Box>
				</Box>
			</Modal>

			{/* Document Preview Modal */}
			<FilePreviewModal
				visible={documentPreview.visible}
				fileKey={documentPreview.fileKey}
				fileName={documentPreview.fileName || 'Document'}
				entity="vendor_contract"
				onClose={closeDocumentPreview}
			/>
		</PageContainer>
	);
}

export default VendorContractDetailsPageV2;
